type BuildStartAppDataUrlOptions = {
	appDataVersion?: string | null;
	appUrl: string;
	domain?: string;
	stack: Window['glStack'];
	timestamp?: number;
};

/**
 * Builds the `/start` URL with cache-busting/version data and the browser
 * domain used by generic-label backends to resolve shop-specific brand styles.
 *
 * @param options {@link BuildStartAppDataUrlOptions} used to construct the request URL.
 * @param options.appDataVersion Production app data version used for CloudFront caching.
 * @param options.appUrl Base Creator application URL.
 * @param options.domain Browser hostname that identifies the current generic-label shop.
 * @param options.stack Runtime stack, used to decide whether to cache-bust or use app data versioning.
 * @param options.timestamp Millisecond timestamp used for non-live cache-busting.
 * @returns `/start` URL including the applicable query parameters.
 */
export function buildStartAppDataUrl({
	appDataVersion,
	appUrl,
	domain,
	stack,
	timestamp = Date.now(),
}: BuildStartAppDataUrlOptions): string {
	const normalizedAppUrl = (
		appUrl.endsWith('/')
			? appUrl
			: `${appUrl}/`
	);
	const searchParams = new URLSearchParams();
	const url = `${normalizedAppUrl}api/app/start`;

	if (stack !== 'live') {
		searchParams.set(
			'version',
			(timestamp / 1000).toString(),
		);
	} else if (appDataVersion) {
		searchParams.set(
			'version',
			appDataVersion,
		);
	}

	const normalizedDomain = domain?.trim().toLowerCase();

	if (normalizedDomain) {
		searchParams.set(
			'shopdomain',
			normalizedDomain,
		);
	}

	const queryString = searchParams.toString();

	return (
		queryString
			? `${url}?${queryString}`
			: url
	);
}
